import { config } from '@iheartjane/config';
import type { AppMode, Nullable } from '@iheartjane/types';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { useEffect } from 'react';

import { cartSize } from '@jane/shared-ecomm/util';
import { api, createUseQueryHook } from '@jane/shared/data-access';
import type { Cart, EmptyCart } from '@jane/shared/types';

import {
  getLocalCartUuid,
  updateLocalCartUuid,
} from '../localCartUuid/localCartUuid';

export type MaybeCart = Cart | EmptyCart;

const cartUrl = () => `${config.apiPath}/carts`;

const getCart = async (
  uuid?: Nullable<string>,
  paymentMethod?: string
): Promise<MaybeCart> => {
  if (!uuid) {
    return {};
  }

  // `payment_method` drives whether the backend computes the Jane Pay (ACH)
  // service fee onto the cart. Without it the cart is optimized as non-Jane-Pay
  // and `jane_pay_service_fee_amount`/`order_total` exclude the fee.
  const queryParams = qs.stringify({
    uuid,
    app_mode: 'kiosk',
    payment_method: paymentMethod,
  });
  const { cart } = await api.get<{ cart: MaybeCart }>(
    `${cartUrl()}?${queryParams}`
  );
  return cart;
};

/**
 * Acts like the typical query to get a cart by its uuid, but coordinates
 *  storing the cart uuid when appropriate.
 */
export const useGetCart = () => {
  const cartUuid = getLocalCartUuid();
  const query = useQuery({
    queryFn: () => getCart(cartUuid),
    queryKey: ['cart', cartUuid],
    notifyOnChangeProps: 'all',
  });

  useEffect(() => {
    // If the cart uuid exists, but the cart has 0 products, reset the uuid
    if (cartUuid && query.isSuccess && cartSize(query.data) === 0) {
      updateLocalCartUuid(null);
    }
  }, [cartUuid, query]);

  return query;
};

/**
 * Fetches the cart optimized as a Jane Pay (ACH) order so the backend-computed
 * `jane_pay_service_fee_amount` and fee-inclusive `order_total` are populated.
 * Used to show the ACH fee on the payment selection screen so the total on the
 * kiosk matches what the shopper is charged.
 */
export const useGetJanePayCart = createUseQueryHook<
  Nullable<string>,
  MaybeCart
>(
  (uuid) => getCart(uuid, 'jane_pay'),
  (uuid) => ['cart', uuid, 'jane_pay'],
  (uuid) => !!uuid,
  { notifyOnChangeProps: 'all' }
);

interface GetCartForUserParams {
  app_mode: AppMode;
  store_id: number | undefined;
  uuid: string;
}

const getCartForUser = async ({
  app_mode,
  store_id,
  uuid,
}: GetCartForUserParams) => {
  const queryParams = qs.stringify({ uuid, app_mode, store_id });
  const { cart } = await api.get<{ cart: MaybeCart }>(
    `${cartUrl()}/get_for_user?${queryParams}`
  );
  return cart;
};

export const useGetCartForUser = createUseQueryHook<
  GetCartForUserParams,
  MaybeCart
>(getCartForUser, (params: GetCartForUserParams) => [
  'cart',
  'getForUser',
  params.uuid,
]);
